import { useState, useMemo } from 'react'
import { getPosSales, getAllPosProducts, getRecipeByProductId, calculateRecipeCost, getOverheads, getOverheadAllocations, getPurchases, getWastageLog } from '../../lib/posStore'
import { useDepartment } from '../../context/DepartmentContext'
import { POS_DEPARTMENTS } from '../../data/posSeed'
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { FileText, Download, TrendingUp, TrendingDown, DollarSign, ShoppingBag, AlertTriangle, Calendar, Filter, Printer } from 'lucide-react'

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4']

function formatR(v) { return `R${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }

function getDateRange(period, customFrom, customTo) {
  const today = new Date()
  const todayStr = today.toISOString().split('T')[0]
  if (period === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo }
  if (period === 'today') return { from: todayStr, to: todayStr }
  if (period === 'week') {
    const d = new Date(today); d.setDate(d.getDate() - 7)
    return { from: d.toISOString().split('T')[0], to: todayStr }
  }
  if (period === 'month') {
    const d = new Date(today); d.setMonth(d.getMonth() - 1)
    return { from: d.toISOString().split('T')[0], to: todayStr }
  }
  return { from: todayStr, to: todayStr }
}

export default function FinancialReports() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('daily')
  const [period, setPeriod] = useState('today')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const { from, to } = getDateRange(period, customFrom, customTo)
  const sales = useMemo(() => getPosSales(currentDepartmentId, from, to).filter(s => !s.voided), [currentDepartmentId, from, to])
  const products = useMemo(() => getAllPosProducts(currentDepartmentId), [currentDepartmentId])
  const purchases = useMemo(() => getPurchases(currentDepartmentId, null, from, to), [currentDepartmentId, from, to])
  const wastage = useMemo(() => getWastageLog(from, to), [from, to])

  // ── Aggregated data ──
  const totalRevenue = sales.reduce((s, sale) => s + (Number(sale.totalRevenue) || 0), 0)
  const totalCost = sales.reduce((s, sale) => s + (Number(sale.totalCost) || 0), 0)
  const grossProfit = totalRevenue - totalCost
  const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0

  const overheads = getOverheads(currentDepartmentId)
  const totalOverhead = overheads.reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const days = Math.max(1, Math.ceil((new Date(to) - new Date(from)) / 86400000) + 1)
  const dailyOverhead = totalOverhead / 26
  const periodOverhead = dailyOverhead * days
  const netProfit = grossProfit - periodOverhead
  const netMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0

  // ── By department ──
  const byDept = useMemo(() => {
    const map = {}
    for (const sale of sales) {
      const dept = sale.department || 'other'
      if (!map[dept]) map[dept] = { revenue: 0, cost: 0, count: 0 }
      map[dept].revenue += Number(sale.totalRevenue) || 0
      map[dept].cost += Number(sale.totalCost) || 0
      map[dept].count++
    }
    return Object.entries(map).map(([dept, data]) => ({
      department: POS_DEPARTMENTS.find(d => d.id === dept)?.label || dept,
      ...data,
      profit: data.revenue - data.cost,
    })).sort((a, b) => b.revenue - a.revenue)
  }, [sales])

  // ── By product ──
  const byProduct = useMemo(() => {
    const map = {}
    for (const sale of sales) {
      if (!sale.items) continue
      for (const item of sale.items) {
        const key = item.productCode || item.productId
        if (!map[key]) map[key] = { code: item.productCode, name: item.name, revenue: 0, cost: 0, qty: 0 }
        map[key].revenue += Number(item.totalPrice) || (Number(item.unitPrice) * Number(item.qty))
        map[key].cost += Number(item.cost) || 0
        map[key].qty += Number(item.qty) || 0
      }
    }
    return Object.values(map).map(p => ({
      ...p,
      profit: p.revenue - p.cost,
      margin: p.revenue > 0 ? ((p.revenue - p.cost) / p.revenue) * 100 : 0,
    }))
  }, [sales])

  const topByRevenue = [...byProduct].sort((a, b) => b.revenue - a.revenue).slice(0, 10)
  const topByMargin = [...byProduct].filter(p => p.revenue > 0).sort((a, b) => b.margin - a.margin).slice(0, 10)
  const lossMakers = byProduct.filter(p => p.profit < 0)

  // ── Daily trend (last 30 days) ──
  const dailyTrend = useMemo(() => {
    const map = {}
    for (const sale of sales) {
      const d = sale.date
      if (!map[d]) map[d] = { date: d, revenue: 0, cost: 0, count: 0 }
      map[d].revenue += Number(sale.totalRevenue) || 0
      map[d].cost += Number(sale.totalCost) || 0
      map[d].count++
    }
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date))
  }, [sales])

  // ── Spend analytics ──
  const totalPurchaseSpend = purchases.reduce((s, p) => {
    const lineTotal = (p.lineItems || []).reduce((ls, li) => ls + (Number(li.qty) * Number(li.unitPrice) || 0), 0)
    return s + lineTotal
  }, 0)

  const tabs = [
    { id: 'daily', label: 'Sales Report' },
    { id: 'pnl', label: 'P&L Statement' },
    { id: 'products', label: 'Product Performance' },
    { id: 'inventory', label: 'Inventory & Purchases' },
    { id: 'charts', label: 'Revenue Trends' },
  ]

  const handleExportCSV = () => {
    const rows = [['Product', 'Qty', 'Revenue', 'Cost', 'Profit', 'Margin %']]
    for (const p of byProduct) {
      rows.push([p.name, p.qty, p.revenue.toFixed(2), p.cost.toFixed(2), p.profit.toFixed(2), p.margin.toFixed(1)])
    }
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `report_${from}_${to}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold">Financial Reports</h1>
        <div className="flex items-center gap-2">
          <button onClick={handleExportCSV} className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-xs">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-xs">
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {['today', 'week', 'month', 'custom'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded text-xs font-medium ${period === p ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 Days' : p === 'month' ? 'Last 30 Days' : 'Custom'}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white" />
            <span className="text-gray-500">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-white" />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Revenue</p>
          <p className="text-xl font-bold text-green-400">{formatR(totalRevenue)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">COGS</p>
          <p className="text-xl font-bold text-red-400">{formatR(totalCost)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Gross Profit</p>
          <p className={`text-xl font-bold ${grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(grossProfit)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Net Profit</p>
          <p className={`text-xl font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(netProfit)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Transactions</p>
          <p className="text-xl font-bold">{sales.length}</p>
        </div>
      </div>

      {/* TAB: Sales Report */}
      {tab === 'daily' && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Sales by Department</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Department</th>
                    <th className="text-right py-2 px-2">Transactions</th>
                    <th className="text-right py-2 px-2">Revenue</th>
                    <th className="text-right py-2 px-2">Cost</th>
                    <th className="text-right py-2 px-2">Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {byDept.map(d => (
                    <tr key={d.department} className="border-b border-gray-800/50">
                      <td className="py-2 px-2">{d.department}</td>
                      <td className="py-2 px-2 text-right">{d.count}</td>
                      <td className="py-2 px-2 text-right text-green-400">{formatR(d.revenue)}</td>
                      <td className="py-2 px-2 text-right text-red-400">{formatR(d.cost)}</td>
                      <td className={`py-2 px-2 text-right ${d.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(d.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {byDept.length === 0 && <p className="text-gray-600 text-sm text-center py-4">No sales data for this period</p>}
            </div>
          </div>

          {lossMakers.length > 0 && (
            <div className="bg-gray-900 border border-red-900/50 rounded-lg p-4">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2 text-red-400"><AlertTriangle size={16} /> Products Sold at a Loss</h3>
              <div className="space-y-1">
                {lossMakers.map(p => (
                  <div key={p.code} className="flex items-center justify-between bg-red-950/20 rounded px-3 py-1.5 text-sm">
                    <span>{p.name} ({p.code})</span>
                    <span className="text-red-400 font-medium">{formatR(p.profit)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: P&L Statement */}
      {tab === 'pnl' && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-4">Profit & Loss Statement</h3>
          <p className="text-xs text-gray-500 mb-4">{from} to {to}</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="font-medium">Revenue</span>
              <span className="text-green-400 font-bold">{formatR(totalRevenue)}</span>
            </div>
            {byDept.map(d => (
              <div key={d.department} className="flex justify-between py-1 pl-4 text-gray-400">
                <span>{d.department}</span>
                <span>{formatR(d.revenue)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="font-medium">Cost of Goods Sold (COGS)</span>
              <span className="text-red-400 font-bold">({formatR(totalCost)})</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800 text-lg">
              <span className="font-bold">Gross Profit</span>
              <span className={`font-bold ${grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(grossProfit)}</span>
            </div>
            <div className="flex justify-between py-1 pl-4 text-gray-400">
              <span>Gross Margin</span>
              <span>{grossMargin.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="font-medium">Overhead (estimated)</span>
              <span className="text-red-400">({formatR(periodOverhead)})</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-800">
              <span className="font-medium">Purchases (supplies)</span>
              <span className="text-gray-400">{formatR(totalPurchaseSpend)}</span>
            </div>
            <div className="flex justify-between py-3 text-xl">
              <span className="font-bold">Net Profit</span>
              <span className={`font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(netProfit)}</span>
            </div>
            <div className="flex justify-between py-1 pl-4 text-gray-400">
              <span>Net Margin</span>
              <span>{netMargin.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Product Performance */}
      {tab === 'products' && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Top 10 by Revenue</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-right py-2 px-2">Qty</th>
                    <th className="text-right py-2 px-2">Revenue</th>
                    <th className="text-right py-2 px-2">Profit</th>
                    <th className="text-right py-2 px-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {topByRevenue.map((p, i) => (
                    <tr key={p.code} className="border-b border-gray-800/50">
                      <td className="py-2 px-2 text-gray-500">{i + 1}</td>
                      <td className="py-2 px-2">{p.name}</td>
                      <td className="py-2 px-2 text-right">{p.qty}</td>
                      <td className="py-2 px-2 text-right text-green-400">{formatR(p.revenue)}</td>
                      <td className={`py-2 px-2 text-right ${p.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(p.profit)}</td>
                      <td className={`py-2 px-2 text-right text-xs ${p.margin > 30 ? 'text-green-400' : p.margin > 10 ? 'text-amber-400' : 'text-red-400'}`}>{p.margin.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {topByRevenue.length === 0 && <p className="text-gray-600 text-sm text-center py-4">No sales data</p>}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">All Products — Ranked by Revenue</h3>
            <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-900">
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-right py-2 px-2">Qty</th>
                    <th className="text-right py-2 px-2">Revenue</th>
                    <th className="text-right py-2 px-2">Cost</th>
                    <th className="text-right py-2 px-2">Profit</th>
                    <th className="text-right py-2 px-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {[...byProduct].sort((a, b) => b.revenue - a.revenue).map(p => (
                    <tr key={p.code} className={`border-b border-gray-800/50 ${p.profit < 0 ? 'bg-red-950/20' : ''}`}>
                      <td className="py-1.5 px-2 font-mono text-xs text-gray-500">{p.code}</td>
                      <td className="py-1.5 px-2">{p.name}</td>
                      <td className="py-1.5 px-2 text-right">{p.qty}</td>
                      <td className="py-1.5 px-2 text-right">{formatR(p.revenue)}</td>
                      <td className="py-1.5 px-2 text-right text-gray-400">{formatR(p.cost)}</td>
                      <td className={`py-1.5 px-2 text-right ${p.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatR(p.profit)}</td>
                      <td className={`py-1.5 px-2 text-right text-xs ${p.margin > 30 ? 'text-green-400' : p.margin > 10 ? 'text-amber-400' : 'text-red-400'}`}>{p.margin.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Inventory & Purchases */}
      {tab === 'inventory' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-400">Total Purchase Spend</p>
              <p className="text-xl font-bold text-red-400">{formatR(totalPurchaseSpend)}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-400">Purchase Orders</p>
              <p className="text-xl font-bold">{purchases.length}</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <p className="text-xs text-gray-400">Wastage Entries</p>
              <p className="text-xl font-bold text-amber-400">{wastage.length}</p>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Spend by Supplier</h3>
            <div className="space-y-1">
              {(() => {
                const map = {}
                for (const p of purchases) {
                  const s = p.supplierName || 'Unknown'
                  const total = (p.lineItems || []).reduce((sum, li) => sum + (Number(li.qty) * Number(li.unitPrice) || 0), 0)
                  map[s] = (map[s] || 0) + total
                }
                return Object.entries(map).sort((a, b) => b[1] - a[1]).map(([name, amount]) => (
                  <div key={name} className="flex items-center justify-between bg-gray-800/50 rounded px-3 py-2 text-sm">
                    <span>{name}</span>
                    <span className="text-gray-300 font-medium">{formatR(amount)}</span>
                  </div>
                ))
              })()}
              {purchases.length === 0 && <p className="text-gray-600 text-sm">No purchase records for this period</p>}
            </div>
          </div>
        </div>
      )}

      {/* TAB: Revenue Trends */}
      {tab === 'charts' && (
        <div className="space-y-4">
          {/* Revenue Line Chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Daily Revenue Trend</h3>
            {dailyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} name="Revenue" />
                  <Line type="monotone" dataKey="cost" stroke="#ef4444" strokeWidth={1} dot={false} name="Cost" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-600 text-sm text-center py-12">No data to chart</p>
            )}
          </div>

          {/* Department Pie Chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Revenue by Department</h3>
            {byDept.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={byDept} dataKey="revenue" nameKey="department" cx="50%" cy="50%" outerRadius={100} label={({ department, percent }) => `${department} ${(percent * 100).toFixed(0)}%`}>
                    {byDept.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px' }} formatter={v => formatR(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-600 text-sm text-center py-12">No data to chart</p>
            )}
          </div>

          {/* Transactions per day bar chart */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
            <h3 className="text-sm font-medium mb-3">Transactions per Day</h3>
            {dailyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#3b82f6" name="Transactions" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-600 text-sm text-center py-12">No data to chart</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
